import theme from './theme-config';

const textStyle = {
    fontSize: 14,
    fontFamily: theme.typography.fontFamily,
    fontWeight: theme.typography.fontWeight.book,
    color: theme.color.jetBlack,
};

export const CHART_TEXT_STYLES = {
    title: { ...textStyle, fontSize: 20, fontWeight: theme.typography.fontWeight.medium },
    subtitle: { ...textStyle, color: theme.color.gray70 },
    label: textStyle,
};

const yAxis = {
    axisLine: {
        show: true,
        lineStyle: {
            color: '#ECEFF1',
            width: 2,
        },
    },
    axisTick: {
        length: 2,
        lineStyle: {
            width: 2,
        },
    },
    axisLabel: textStyle,
    splitLine: {
        show: true,
        lineStyle: {
            color: '#ECEFF1',
            opacity: 0.8,
        },
    },
    nameLocation: 'middle',
    nameTextStyle: {
        textStyle,
        rich: {},
    },
};

/**
 * ECharts'(https://github.com/apache/incubator-echarts) theme object
 * It used ECharts internal format
 * Docs - https://ecomfe.github.io/echarts-doc/public/en/option.html
 * Theme builder - http://echarts.baidu.com/theme-builder/
 */
export default {
    // Semantic theme configuration for charts common components
    // Please put here only configuration options with semantic meaning
    map: {
        locationDefaultColor: '#d6d6d6',
        locationSelectedColor: '#50C3F7',
        locationLabelColor: '#333333',
        locationBorderColor: 'white',
        locationInRangeColorSpectrum: [
            '#199949',
            '#20C05C',
            '#4CCC7C',
            '#82DBA3',
            '#FBDD39',
            '#F7B600',
            '#F18602',
            '#E86D0C',
        ],
        locationOutOfRangeColor: '#ECEFF1',
        sliderAxisColor: '#ECEFF1',
        sliderPointColor: '#C1C8CB',
        sliderPlayButtonColor: '#C1C8CB',
        sliderPlayButtonBorderColor: '#C1C8CB',
        sliderPlayButtonHoverColor: '#96C984',
        sliderPlayButtonBorderHoverColor: '#96C984',
    },
    bar: {
        seriesColorSpectrum: [
            '#9575CD',
            '#82DBA3',
            '#4CCC7C',
            '#20C05C',
            '#199949',
            '#FBDD39',
            '#F7B600',
            '#F18602',
            '#E86D0C',
            '#8D6E63',
        ],
        series: {
            type: 'bar',
            barCategoryGap: 10,
        },
    },
    pie: {
        seriesColorSpectrum: [
            '#9575CD',
            '#82DBA3',
            '#4CCC7C',
            '#20C05C',
            '#199949',
            '#FBDD39',
            '#F7B600',
            '#F18602',
            '#E86D0C',
            '#8D6E63',
        ],
    },

    line: {
        grid: {
            top: 90,
            left: 90,
        },
        colors: [
            {
                type: 'linear',
                x: 0,
                y: 1,
                x2: 0,
                y2: 0,
                colorStops: [
                    {
                        offset: 0,
                        color: '#86CA46',
                    },
                    {
                        offset: 1,
                        color: '#0DA384',
                    },
                ],
            },
            {
                type: 'linear',
                x: 0,
                y: 1,
                x2: 0,
                y2: 0,
                colorStops: [
                    {
                        offset: 0,
                        color: '#FAB93A',
                    },
                    {
                        offset: 1,
                        color: '#F06054',
                    },
                ],
            },
        ],
    },

    // Put below any arbitrary configuration copy-pasted from echarts API
    loading: {
        text: 'Loading...',
        color: '#20C05C',
        textColor: '#000',
        maskColor: 'rgba(255, 255, 255, 0.8)',
        zlevel: 0,
    },
    textStyle,
    title: {
        top: 0,
        left: 'center',
        backgroundColor: 'rgba(255, 255, 255, .8)',
        textStyle: CHART_TEXT_STYLES.title,
        subtextStyle: CHART_TEXT_STYLES.subtitle,
    },
    noData: {
        top: '45%',
        left: 'center',
        textStyle: {
            ...textStyle,
            fontSize: 26,
        },
    },
    toolbox: {
        borderColor: '#ccc',
        padding: 5,
        itemGap: 8,
        iconStyle: { borderColor: '#666' },
        emphasis: { iconStyle: { borderColor: '#3E98C5' } },
        feature: {
            saveAsImage: {
                excludeComponents: ['toolbox', 'graphic'],
                pixelRatio: 2,
            },
        },
    },
    legend: {
        z: 10,
        top: 60,
        right: 30,
        orient: 'horizontal',
    },
    tooltip: {
        axisPointer: { crossStyle: { color: '#999' } },
        borderWidth: 1,
        defaultBorderColor: '#fff',
        triggerOn: 'mousemove|click',
        displayTransition: false,
        backgroundColor: 'white',
        extraCssText:
            'box-shadow: 0 10px 10px 0 rgba(0,0,0,0.05), 0 0 10px 0 rgba(0,0,0,0.10), 0 20px 20px 0 rgba(0,0,0,0.08); border-radius: 4px;',
        padding: [16, 20],
        textStyle,
    },
    xAxis: {
        axisLine: {
            show: true,
            lineStyle: {
                color: '#ECEFF1',
                width: 2,
            },
        },
        axisLabel: textStyle,
        axisTick: {
            length: 2,
            lineStyle: {
                width: 2,
            },
        },
        splitLine: {
            show: true,
            lineStyle: {
                color: '#ECEFF1',
                opacity: 0.8,
            },
        },
        nameLocation: 'center',
        nameTextStyle: textStyle,
        nameGap: 45,
    },
    yAxis: [
        {
            ...yAxis,
            id: 0,
        },
        {
            ...yAxis,
            id: 1,
            position: 'right',
        },
    ],
    dataZoom: {
        borderRadius: 3,
        selectedDataBackground: {
            lineStyle: { color: '#8fb0f7', width: 0.5 },
            areaStyle: { color: '#8fb0f7', opacity: 0.2 },
        },
        moveHandleStyle: { color: '#D2DBEE', opacity: 0.7 },
        brushStyle: { color: 'rgba(135,175,274,0.15)' },
        emphasis: {
            handleStyle: { borderColor: '#8FB0F7' },
            moveHandleStyle: { color: '#8FB0F7', opacity: 0.7 },
        },
        defaultLocationEdgeGap: 7,
        type: 'slider',
        textStyle: {
            fontSize: 12,
        },
        backgroundColor: '#F5F7F8',
        fillerColor: 'rgba(194,201,204, 0.25)',
        borderColor: 'transparent',
        dataBackground: {
            lineStyle: {
                color: '#ACB8BE',
            },
            areaStyle: {
                color: '#DDE3E6',
            },
        },
        handleStyle: {
            color: '#C2C9CC',
        },
    },
    markLine: {
        symbol: '',
        lineStyle: {
            color: '#20A050',
            width: 2,
        },
        label: {
            show: false,
        },
        animation: false,
    },
    grid: {
        left: '10%',
        top: 60,
        bottom: 70,
        outerBoundsMode: 'none',
    },
    categoryAxis: {
        axisLabel: { textMargin: [0, 0] },
        axisLine: { onZero: true },
        nameTextStyle: { textMargin: 0 },
    },
    valueAxis: {
        axisLabel: { textMargin: [0, 0] },
        axisLine: { onZero: true },
        nameTextStyle: { textMargin: 0 },
    },
    timeAxis: {
        axisLabel: { textMargin: [0, 0] },
        axisLine: { onZero: true },
        nameTextStyle: { textMargin: 0 },
    },
    logAxis: {
        axisLabel: { textMargin: [0, 0] },
        axisLine: { onZero: true },
        nameTextStyle: { textMargin: 0 },
    },
    visualMap: {
        color: ['#bf444c', '#d88273', '#f6efa6'],
        inactive: ['rgba(0,0,0,0)'],
        indicatorStyle: {
            shadowColor: 'rgba(0,0,0,0.2)',
        },
        backgroundColor: 'rgba(0,0,0,0)',
        borderColor: '#ccc',
        contentColor: '#5793f3',
        inactiveColor: '#aaa',
        padding: 5,
        textStyle: {
            color: '#333',
        },
    },
    treemap: {
        left: 'center',
        top: 'middle',
        width: '80%',
        height: '80%',
        scaleLimit: {
            max: null,
            min: null,
        },
        roamTrigger: 'selfRect',
        breadcrumb: {
            top: 'bottom',
            bottom: null,
            itemStyle: {
                color: 'rgba(0,0,0,0.7)',
                textStyle: {
                    color: '#fff',
                },
            },
            emphasis: {
                itemStyle: {
                    color: 'rgba(0,0,0,0.9)',
                },
            },
        },
    },
    timeline: {
        padding: 5,
        borderColor: '#ccc',
        lineStyle: {
            color: '#DAE1F5',
        },
        label: {
            color: '#A4B1D7',
        },
        itemStyle: {
            color: '#A4B1D7',
            borderWidth: 1,
        },
        checkpointStyle: {
            color: '#316bf3',
            borderColor: '#fff',
            borderWidth: 2,
            shadowBlur: 2,
            shadowOffsetX: 1,
            shadowOffsetY: 1,
            shadowColor: 'rgba(0, 0, 0, 0.3)',
        },
        controlStyle: {
            playIcon:
                'path://M31.6,53C17.5,53,6,41.5,6,27.4S17.5,1.8,31.6,1.8C45.7,1.8,57.2,13.3,57.2,27.4S45.7,53,31.6,53z M31.6,3.3 C18.4,3.3,7.5,14.1,7.5,27.4c0,13.3,10.8,24.1,24.1,24.1C44.9,51.5,55.7,40.7,55.7,27.4C55.7,14.1,44.9,3.3,31.6,3.3z M24.9,21.3 c0-2.2,1.6-3.1,3.5-2l10.5,6.1c1.899,1.1,1.899,2.9,0,4l-10.5,6.1c-1.9,1.1-3.5,0.2-3.5-2V21.3z',
            stopIcon:
                'path://M30.9,53.2C16.8,53.2,5.3,41.7,5.3,27.6S16.8,2,30.9,2C45,2,56.4,13.5,56.4,27.6S45,53.2,30.9,53.2z M30.9,3.5C17.6,3.5,6.8,14.4,6.8,27.6c0,13.3,10.8,24.1,24.101,24.1C44.2,51.7,55,40.9,55,27.6C54.9,14.4,44.1,3.5,30.9,3.5z M36.9,35.8c0,0.601-0.4,1-0.9,1h-1.3c-0.5,0-0.9-0.399-0.9-1V19.5c0-0.6,0.4-1,0.9-1H36c0.5,0,0.9,0.4,0.9,1V35.8z M27.8,35.8 c0,0.601-0.4,1-0.9,1h-1.3c-0.5,0-0.9-0.399-0.9-1V19.5c0-0.6,0.4-1,0.9-1H27c0.5,0,0.9,0.4,0.9,1L27.8,35.8L27.8,35.8z',
            nextIcon:
                'M2,18.5A1.52,1.52,0,0,1,.92,18a1.49,1.49,0,0,1,0-2.12L7.81,9.36,1,3.11A1.5,1.5,0,1,1,3,.89l8,7.34a1.48,1.48,0,0,1,.49,1.09,1.51,1.51,0,0,1-.46,1.1L3,18.08A1.5,1.5,0,0,1,2,18.5Z',
            prevIcon:
                'M10,.5A1.52,1.52,0,0,1,11.08,1a1.49,1.49,0,0,1,0,2.12L4.19,9.64,11,15.89a1.5,1.5,0,1,1-2,2.22L1,10.77A1.48,1.48,0,0,1,.5,9.68,1.51,1.51,0,0,1,1,8.58L9,.92A1.5,1.5,0,0,1,10,.5Z',
            color: '#A4B1D7',
            borderColor: '#A4B1D7',
            borderWidth: 1,
        },
        emphasis: {
            label: {
                color: '#6f778d',
            },
            itemStyle: {
                color: '#316BF3',
            },
            controlStyle: {
                color: '#316BF3',
                borderColor: '#316BF3',
                borderWidth: 2,
            },
        },
        progress: {
            lineStyle: {
                color: '#316BF3',
            },
            itemStyle: {
                color: '#316BF3',
            },
            label: {
                color: '#6f778d',
            },
        },
    },
};
